/**
 * Company Files + My files helpers (P1 foundation).
 *
 * Firestore: companies/{companyId}/files_nodes/{nodeId}
 * Storage:
 *   companies/{companyId}/files/company/{fileName}
 *   companies/{companyId}/files/user/{userId}/{fileName}
 */

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import {
  deleteObject,
  getBytes,
  getDownloadURL,
  ref,
  uploadBytes,
} from 'firebase/storage';
import { db, storage } from '../firebase';

/** Root folder sentinel — empty string (not null) for stable equality queries. */
export const FILES_ROOT_PARENT_ID = '';

export const FILES_OWNER_SCOPE_COMPANY = 'company';
export const FILES_OWNER_SCOPE_USER = 'user';

export const FILES_KIND_FOLDER = 'folder';
export const FILES_KIND_FILE = 'file';

export const FILES_SOURCE_UPLOAD = 'upload';
export const FILES_SOURCE_IMAGE_EDITOR = 'image_editor';
export const FILES_SOURCE_DRIVE_IMPORT = 'drive_import';
export const FILES_SOURCE_PROJECT_LINK = 'project_link';

/**
 * @param {string} name
 * @returns {string}
 */
export function sanitizeFilesNodeName(name) {
  const trimmed = typeof name === 'string' ? name.trim() : '';
  if (!trimmed) return 'untitled';
  return trimmed.replace(/[\\/]/g, '-').slice(0, 200);
}

/**
 * @param {string} name
 * @returns {string}
 */
export function sanitizeFilesStorageSegment(name) {
  const base = sanitizeFilesNodeName(name)
    .replace(/[^a-zA-Z0-9._-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
  return (base || 'file').slice(0, 80);
}

/**
 * Resolve the Storage object path for a Files blob (P1 layout).
 *
 * @param {{
 *   companyId: string,
 *   ownerScope: 'company'|'user',
 *   ownerUid?: string|null,
 *   fileName: string,
 * }} params
 * @returns {string}
 */
export function buildFilesBlobStoragePath(params) {
  const companyId = String(params.companyId || '').trim();
  const fileName = String(params.fileName || '').trim();
  const ownerScope = params.ownerScope;
  if (!companyId || !fileName || !ownerScope) {
    throw new Error('companyId, fileName, and ownerScope are required.');
  }
  if (ownerScope === FILES_OWNER_SCOPE_USER) {
    const ownerUid = String(params.ownerUid || '').trim();
    if (!ownerUid) throw new Error('ownerUid is required for My files.');
    return `companies/${companyId}/files/user/${ownerUid}/${fileName}`;
  }
  if (ownerScope === FILES_OWNER_SCOPE_COMPANY) {
    return `companies/${companyId}/files/company/${fileName}`;
  }
  throw new Error(`Unsupported ownerScope: ${ownerScope}`);
}

/**
 * @param {{
 *   companyId: string,
 *   ownerScope: 'company'|'user',
 *   ownerUid?: string|null,
 *   storagePath: string,
 * }} params
 * @returns {boolean}
 */
export function storagePathMatchesOwnerScope(params) {
  const companyId = String(params.companyId || '').trim();
  const storagePath = String(params.storagePath || '').trim();
  const ownerScope = params.ownerScope;
  if (!companyId || !storagePath || !ownerScope) return false;
  if (ownerScope === FILES_OWNER_SCOPE_COMPANY) {
    const prefix = `companies/${companyId}/files/company/`;
    return storagePath.startsWith(prefix) && storagePath.length > prefix.length;
  }
  if (ownerScope === FILES_OWNER_SCOPE_USER) {
    const ownerUid = String(params.ownerUid || '').trim();
    if (!ownerUid) return false;
    const prefix = `companies/${companyId}/files/user/${ownerUid}/`;
    return storagePath.startsWith(prefix) && storagePath.length > prefix.length;
  }
  return false;
}

/**
 * @param {{
 *   companyId: string,
 *   ownerScope: 'company'|'user',
 *   ownerUid?: string|null,
 *   storagePath: string,
 *   label?: string,
 * }} params
 */
export function assertFilesStoragePathMatchesOwnerScope(params) {
  if (
    !storagePathMatchesOwnerScope({
      companyId: params.companyId,
      ownerScope: params.ownerScope,
      ownerUid: params.ownerUid,
      storagePath: params.storagePath,
    })
  ) {
    const label = params.label ? `${params.label} ` : '';
    throw new Error(
      `${label}storage path does not match ownerScope "${params.ownerScope}".`,
    );
  }
}

/**
 * Collect all Storage paths referenced by a files_nodes file row (layered IE projects
 * store preview + per-layer blobs outside the primary storagePath field).
 *
 * @param {{ storagePath?: string|null, previewStoragePath?: string|null, editorLayerBlobs?: Array<{ storagePath?: string }>, editorDocumentManifest?: { previewStoragePath?: string|null, layers?: Array<{ storagePath?: string }> } }} node
 * @returns {string[]}
 */
export function collectFilesNodeStoragePaths(node) {
  /** @type {Set<string>} */
  const paths = new Set();
  const add = (value) => {
    if (typeof value === 'string' && value.trim()) paths.add(value.trim());
  };
  add(node?.storagePath);
  add(node?.previewStoragePath);
  if (Array.isArray(node?.editorLayerBlobs)) {
    for (const row of node.editorLayerBlobs) add(row?.storagePath);
  }
  const manifest = node?.editorDocumentManifest;
  if (manifest && typeof manifest === 'object') {
    add(manifest.previewStoragePath);
    if (Array.isArray(manifest.layers)) {
      for (const layer of manifest.layers) add(layer?.storagePath);
    }
  }
  return [...paths];
}

/**
 * @param {string} companyId
 * @returns {import('firebase/firestore').CollectionReference}
 */
export function filesNodesCollection(companyId) {
  return collection(db, 'companies', companyId, 'files_nodes');
}

/**
 * @param {{
 *   companyId: string,
 *   ownerScope: 'company'|'user',
 *   ownerUid?: string|null,
 *   parentId?: string|null,
 * }} params
 * @returns {Promise<Array<{ id: string } & Record<string, unknown>>>}
 */
export async function listFilesChildren(params) {
  const companyId =
    typeof params.companyId === 'string' ? params.companyId.trim() : '';
  const ownerScope = params.ownerScope;
  const parentId =
    params.parentId === null || params.parentId === undefined
      ? FILES_ROOT_PARENT_ID
      : String(params.parentId);
  if (!companyId || !ownerScope) {
    throw new Error('companyId and ownerScope are required.');
  }

  /** @type {import('firebase/firestore').QueryConstraint[]} */
  const constraints = [
    where('ownerScope', '==', ownerScope),
    where('parentId', '==', parentId),
  ];
  if (ownerScope === FILES_OWNER_SCOPE_USER) {
    const ownerUid =
      typeof params.ownerUid === 'string' ? params.ownerUid.trim() : '';
    if (!ownerUid) throw new Error('ownerUid is required for My files.');
    constraints.push(where('ownerUid', '==', ownerUid));
  }

  const snap = await getDocs(query(filesNodesCollection(companyId), ...constraints));
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  rows.sort((a, b) => {
    const ak = a.kind === FILES_KIND_FOLDER ? 0 : 1;
    const bk = b.kind === FILES_KIND_FOLDER ? 0 : 1;
    if (ak !== bk) return ak - bk;
    return String(a.name || '').localeCompare(String(b.name || ''), undefined, {
      sensitivity: 'base',
    });
  });
  return rows;
}

/**
 * @param {{
 *   companyId: string,
 *   name: string,
 *   ownerScope: 'company'|'user',
 *   ownerUid?: string|null,
 *   parentId?: string|null,
 *   uid?: string|null,
 * }} params
 */
export async function createFilesFolder(params) {
  const companyId =
    typeof params.companyId === 'string' ? params.companyId.trim() : '';
  const name = sanitizeFilesNodeName(params.name);
  const ownerScope = params.ownerScope;
  const parentId =
    params.parentId === null || params.parentId === undefined
      ? FILES_ROOT_PARENT_ID
      : String(params.parentId);
  const uid = params.uid || null;

  if (!companyId) throw new Error('companyId is required.');
  if (ownerScope === FILES_OWNER_SCOPE_USER) {
    const ownerUid =
      typeof params.ownerUid === 'string' ? params.ownerUid.trim() : '';
    if (!ownerUid) throw new Error('ownerUid is required for My files.');
    if (uid && ownerUid !== uid) {
      throw new Error('Cannot create My files folder for another user.');
    }
  }

  const payload = {
    kind: FILES_KIND_FOLDER,
    parentId,
    name,
    ownerScope,
    ownerUid:
      ownerScope === FILES_OWNER_SCOPE_USER
        ? String(params.ownerUid).trim()
        : null,
    storagePath: null,
    mimeType: null,
    bytes: null,
    source: null,
    linkedProjectId: null,
    linkedAssetId: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdByUid: uid,
  };

  const refDoc = await addDoc(filesNodesCollection(companyId), payload);
  return { id: refDoc.id, ...payload };
}

/**
 * @param {{
 *   companyId: string,
 *   file: File|Blob,
 *   fileName?: string,
 *   ownerScope: 'company'|'user',
 *   ownerUid?: string|null,
 *   parentId?: string|null,
 *   uid?: string|null,
 *   source?: string,
 * }} params
 */
export async function uploadFilesNode(params) {
  const companyId =
    typeof params.companyId === 'string' ? params.companyId.trim() : '';
  const file = params.file;
  const ownerScope = params.ownerScope;
  const parentId =
    params.parentId === null || params.parentId === undefined
      ? FILES_ROOT_PARENT_ID
      : String(params.parentId);
  const uid = params.uid || null;
  const source = params.source || FILES_SOURCE_UPLOAD;

  if (!companyId || !file) {
    throw new Error('companyId and file are required.');
  }

  const displayName = sanitizeFilesNodeName(
    params.fileName ||
      (typeof file.name === 'string' && file.name) ||
      'upload',
  );
  const stem = sanitizeFilesStorageSegment(displayName);
  const stamp = Date.now();
  const storageFileName = `${stem}_${stamp}`;

  const ownerUid =
    typeof params.ownerUid === 'string' ? params.ownerUid.trim() : '';
  if (ownerScope === FILES_OWNER_SCOPE_USER) {
    if (!ownerUid) throw new Error('ownerUid is required for My files.');
    if (uid && ownerUid !== uid) {
      throw new Error('Cannot upload into another user\'s My files.');
    }
  }
  const storagePath = buildFilesBlobStoragePath({
    companyId,
    ownerScope,
    ownerUid,
    fileName: storageFileName,
  });

  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, file, {
    contentType: file.type || 'application/octet-stream',
  });
  const downloadUrl = await getDownloadURL(storageRef);

  const payload = {
    kind: FILES_KIND_FILE,
    parentId,
    name: displayName,
    ownerScope,
    ownerUid:
      ownerScope === FILES_OWNER_SCOPE_USER
        ? String(params.ownerUid).trim()
        : null,
    storagePath,
    downloadUrl,
    mimeType: file.type || 'application/octet-stream',
    bytes: typeof file.size === 'number' ? file.size : null,
    source,
    linkedProjectId: null,
    linkedAssetId: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdByUid: uid,
  };

  const refDoc = await addDoc(filesNodesCollection(companyId), payload);
  return { id: refDoc.id, downloadUrl, storagePath, ...payload };
}

/**
 * Client-side write gate mirroring files_nodes rules (studio company / owner My files).
 * Firestore still enforces. When `role` is omitted for company nodes, returns true
 * (defer to server). My files still require matching uid when uid is provided.
 *
 * @param {{
 *   node: { ownerScope?: string, ownerUid?: string|null },
 *   uid?: string|null,
 *   role?: string|null,
 * }} params
 * @returns {boolean}
 */
export function canWriteFilesNode(params) {
  const node = params.node;
  const uid = params.uid || null;
  const role = typeof params.role === 'string' ? params.role : null;
  if (!node || typeof node !== 'object') return false;
  if (role === 'super_admin') return true;
  if (node.ownerScope === FILES_OWNER_SCOPE_COMPANY) {
    if (role == null) return true;
    return role === 'company_admin' || role === 'developer';
  }
  if (node.ownerScope === FILES_OWNER_SCOPE_USER) {
    if (!uid) return role == null;
    return node.ownerUid === uid;
  }
  return false;
}

/**
 * Delete a file or empty folder node. File blobs are removed from Storage.
 * @param {{
 *   companyId: string,
 *   node: { id: string, kind?: string, storagePath?: string|null, ownerScope?: string, ownerUid?: string|null, parentId?: string },
 *   uid?: string|null,
 *   role?: string|null,
 * }} params
 */
export async function deleteFilesNode(params) {
  const companyId =
    typeof params.companyId === 'string' ? params.companyId.trim() : '';
  const node = params.node;
  if (!companyId || !node?.id) {
    throw new Error('companyId and node.id are required.');
  }
  if (!canWriteFilesNode({ node, uid: params.uid, role: params.role })) {
    throw new Error('You do not have permission to delete this item.');
  }

  if (node.kind === FILES_KIND_FOLDER) {
    const children = await listFilesChildren({
      companyId,
      ownerScope: node.ownerScope,
      ownerUid: node.ownerUid,
      parentId: node.id,
    });
    if (children.length > 0) {
      throw new Error('Folder is not empty. Delete or move children first.');
    }
  }

  if (node.kind === FILES_KIND_FILE) {
    const blobPaths = collectFilesNodeStoragePaths(node);
    for (const storagePath of blobPaths) {
      try {
        if (
          !storagePathMatchesOwnerScope({
            companyId,
            ownerScope: node.ownerScope,
            ownerUid: node.ownerUid,
            storagePath,
          })
        ) {
          console.warn(
            'Files delete: skipping Storage blob — path/ownerScope mismatch:',
            storagePath,
          );
          continue;
        }
        await deleteObject(ref(storage, storagePath));
      } catch (err) {
        // Missing object should not block metadata delete.
        if (err?.code === 'storage/object-not-found') continue;
        // Legacy rows may reference company blobs on user-scoped nodes; drop metadata.
        if (err?.code === 'storage/unauthorized') {
          console.warn('Files delete: Storage unauthorized for', storagePath);
          continue;
        }
        throw err;
      }
    }
  }

  await deleteDoc(doc(db, 'companies', companyId, 'files_nodes', node.id));
}

/**
 * @param {{
 *   companyId: string,
 *   folderId: string,
 *   ownerScope: 'company'|'user',
 *   ownerUid?: string|null,
 * }} params
 * @returns {Promise<Set<string>>}
 */
export async function collectFilesDescendantIds(params) {
  const ids = new Set();
  const queue = [params.folderId];
  while (queue.length) {
    const parentId = queue.shift();
    const children = await listFilesChildren({
      companyId: params.companyId,
      ownerScope: params.ownerScope,
      ownerUid: params.ownerUid,
      parentId,
    });
    for (const child of children) {
      ids.add(child.id);
      if (child.kind === FILES_KIND_FOLDER) queue.push(child.id);
    }
  }
  return ids;
}

/**
 * Move a node under a new parent (same ownerScope). ACL enforced by Firestore update.
 * @param {{
 *   companyId: string,
 *   node: { id: string, kind?: string, ownerScope?: string, ownerUid?: string|null, parentId?: string },
 *   targetParentId?: string|null,
 *   uid?: string|null,
 *   role?: string|null,
 * }} params
 */
export async function moveFilesNode(params) {
  const companyId =
    typeof params.companyId === 'string' ? params.companyId.trim() : '';
  const node = params.node;
  const targetParentId =
    params.targetParentId === null || params.targetParentId === undefined
      ? FILES_ROOT_PARENT_ID
      : String(params.targetParentId);

  if (!companyId || !node?.id) {
    throw new Error('companyId and node.id are required.');
  }
  if (!canWriteFilesNode({ node, uid: params.uid, role: params.role })) {
    throw new Error('You do not have permission to move this item.');
  }
  if (targetParentId === node.id) {
    throw new Error('Cannot move a folder into itself.');
  }
  if (String(node.parentId ?? FILES_ROOT_PARENT_ID) === targetParentId) {
    return { id: node.id, parentId: targetParentId, unchanged: true };
  }

  if (node.kind === FILES_KIND_FOLDER) {
    const descendants = await collectFilesDescendantIds({
      companyId,
      folderId: node.id,
      ownerScope: node.ownerScope,
      ownerUid: node.ownerUid,
    });
    if (descendants.has(targetParentId)) {
      throw new Error('Cannot move a folder into one of its descendants.');
    }
  }

  await updateDoc(doc(db, 'companies', companyId, 'files_nodes', node.id), {
    parentId: targetParentId,
    updatedAt: serverTimestamp(),
  });
  return { id: node.id, parentId: targetParentId, unchanged: false };
}

/**
 * Duplicate a node. Files get a new Storage object (not a shared path).
 * Folders are copied recursively (new folder + copied children).
 *
 * @param {{
 *   companyId: string,
 *   node: { id: string } & Record<string, unknown>,
 *   targetParentId?: string|null,
 *   uid?: string|null,
 *   role?: string|null,
 *   nameSuffix?: string,
 * }} params
 */
export async function copyFilesNode(params) {
  const companyId =
    typeof params.companyId === 'string' ? params.companyId.trim() : '';
  const node = params.node;
  const uid = params.uid || null;
  const targetParentId =
    params.targetParentId === null || params.targetParentId === undefined
      ? String(node.parentId ?? FILES_ROOT_PARENT_ID)
      : String(params.targetParentId);
  const suffix =
    typeof params.nameSuffix === 'string' ? params.nameSuffix : ' copy';

  if (!companyId || !node?.id) {
    throw new Error('companyId and node.id are required.');
  }
  if (!canWriteFilesNode({ node, uid, role: params.role })) {
    throw new Error('You do not have permission to copy this item.');
  }

  const baseName = sanitizeFilesNodeName(
    (typeof node.name === 'string' && node.name) || 'untitled',
  );
  const copyName = sanitizeFilesNodeName(`${baseName}${suffix}`);

  if (node.kind === FILES_KIND_FOLDER) {
    const folder = await createFilesFolder({
      companyId,
      name: copyName,
      ownerScope: node.ownerScope,
      ownerUid: node.ownerUid,
      parentId: targetParentId,
      uid,
    });
    const children = await listFilesChildren({
      companyId,
      ownerScope: node.ownerScope,
      ownerUid: node.ownerUid,
      parentId: node.id,
    });
    for (const child of children) {
      await copyFilesNode({
        companyId,
        node: child,
        targetParentId: folder.id,
        uid,
        role: params.role,
        nameSuffix: '',
      });
    }
    return { id: folder.id, kind: FILES_KIND_FOLDER, name: copyName };
  }

  if (node.kind !== FILES_KIND_FILE) {
    throw new Error('Unsupported node kind for copy.');
  }
  if (!node.storagePath && !node.downloadUrl) {
    throw new Error('File has no Storage object to duplicate.');
  }

  let bytes;
  let contentType =
    (typeof node.mimeType === 'string' && node.mimeType) ||
    'application/octet-stream';
  if (node.storagePath) {
    bytes = await getBytes(ref(storage, node.storagePath));
  } else {
    const res = await fetch(String(node.downloadUrl));
    if (!res.ok) throw new Error('Could not download file to copy.');
    const buf = await res.arrayBuffer();
    bytes = new Uint8Array(buf);
    const headerType = res.headers.get('content-type');
    if (headerType) contentType = headerType;
  }

  const blob = new Blob([bytes], { type: contentType });
  return uploadFilesNode({
    companyId,
    file: blob,
    fileName: copyName,
    ownerScope: node.ownerScope,
    ownerUid: node.ownerUid,
    parentId: targetParentId,
    uid,
    source: node.source || FILES_SOURCE_UPLOAD,
  });
}

/**
 * Run a per-item bulk op; continues after failures (partial success).
 *
 * @template T
 * @param {{
 *   items: T[],
 *   runOne: (item: T) => Promise<unknown>,
 * }} params
 * @returns {Promise<{ ok: T[], failed: Array<{ item: T, error: string }> }>}
 */
export async function runBulkFilesOperation(params) {
  const items = Array.isArray(params.items) ? params.items : [];
  /** @type {T[]} */
  const ok = [];
  /** @type {Array<{ item: T, error: string }>} */
  const failed = [];
  for (const item of items) {
    try {
      await params.runOne(item);
      ok.push(item);
    } catch (err) {
      failed.push({
        item,
        error: err?.message || String(err) || 'Operation failed.',
      });
    }
  }
  return { ok, failed };
}

/**
 * @param {{ ok: unknown[], failed: Array<{ item: { name?: string, id?: string }, error: string }> }} result
 * @param {string} verb past-tense e.g. "moved"
 */
export function formatBulkFilesResultMessage(result, verb) {
  const okN = result.ok?.length || 0;
  const failN = result.failed?.length || 0;
  if (failN === 0) return `${okN} item${okN === 1 ? '' : 's'} ${verb}.`;
  const details = result.failed
    .slice(0, 3)
    .map((f) => {
      const label =
        (typeof f.item?.name === 'string' && f.item.name) ||
        f.item?.id ||
        'item';
      return `${label}: ${f.error}`;
    })
    .join(' · ');
  const more = failN > 3 ? ` (+${failN - 3} more)` : '';
  return `${okN} ${verb}, ${failN} failed. ${details}${more}`;
}
